import { IconName } from '@upsales/components';
import T from 'Components/Helpers/translate';
import Relation, { RELATION } from 'App/resources/Model/EntityRelation';

export type RelationListEntity = 'Order' | 'Appointment' | 'Activity' | 'ProjectPlan' | 'Ticket' | 'Agreement';

export type RelationListItem = {
	entity: RelationListEntity;
	id: number;
	name: string | null;
};

type ListType = Exclude<RELATION, 'udo'>;

const ENTITY_BY_TYPE: Record<ListType, RelationListEntity> = {
	opportunity: 'Order',
	appointment: 'Appointment',
	activity: 'Activity',
	projectPlan: 'ProjectPlan',
	ticket: 'Ticket',
	agreement: 'Agreement'
};

const ICON_BY_TYPE: Record<ListType, IconName> = {
	opportunity: 'opportunity',
	appointment: 'calendar',
	activity: 'activity',
	projectPlan: 'project',
	ticket: 'customer-support',
	agreement: 'subscription'
};

const typeOf = (entity: RelationListEntity) =>
	(Object.keys(ENTITY_BY_TYPE) as ListType[]).find(type => ENTITY_BY_TYPE[type] === entity);

export const isInList = (list: RelationListItem[], rel: Pick<Relation, 'type' | 'id'>) =>
	list.some(item => item.id === rel.id && item.entity === ENTITY_BY_TYPE[rel.type as ListType]);

export const removeFromList = (list: RelationListItem[], rel: Pick<Relation, 'type' | 'id'>) =>
	list.filter(item => !(item.id === rel.id && item.entity === ENTITY_BY_TYPE[rel.type as ListType]));

export const toggleInList = (list: RelationListItem[], rel: Relation): RelationListItem[] =>
	isInList(list, rel)
		? removeFromList(list, rel)
		: [...list, { entity: ENTITY_BY_TYPE[rel.type as ListType], id: rel.id, name: rel.title }];

export const removeTypesFromList = (list: RelationListItem[], types: ListType[]) =>
	list.filter(item => !types.some(type => ENTITY_BY_TYPE[type] === item.entity));

export const listToRelations = (list: RelationListItem[], known: Relation[]): Relation[] =>
	list.flatMap(item => {
		const type = typeOf(item.entity);
		if (!type) {
			return [];
		}
		const found = known.find(rel => rel.type === type && rel.id === item.id);
		return [
			found ?? {
				type,
				id: item.id,
				title: item.name ?? T('default.noAccess'),
				subtitle: '',
				icon: ICON_BY_TYPE[type],
				withContact: false,
				date: null
			}
		];
	});
